import { emptyProgress } from '../ports/progress.ts';
import type {
  AnsweredEntry,
  BookmarkEntry,
  Progress,
  ProgressListener,
  ProgressRepo,
} from '../ports/progress.ts';
import type { KeyValueStorage } from '../ports/storage.ts';

export const CURRENT_PROGRESS_VERSION = 1;

/** Upgrades a stored payload from version N to N + 1. May throw on bad input. */
export type Migration = (raw: Record<string, unknown>) => Record<string, unknown>;

export interface LocalProgressRepoOptions {
  /** Key prefix. Default `dcs`. */
  namespace?: string;
  /** Extra or overriding migrations, keyed by the version they upgrade FROM. */
  migrations?: Readonly<Record<number, Migration>>;
  /** JSON length above which `answered` is compacted. Default 100_000. */
  maxBytes?: number;
}

/**
 * v0 (unversioned, early prototype): `{ answered: { [id]: boolean }, wrong: id[] }`.
 * v1: entries become `{ lastChoice, correct, at }`; unknown choice/time are -1/0.
 */
const migrateV0: Migration = (raw) => {
  const old = (raw.answered ?? {}) as Record<string, unknown>;
  const answered: Record<string, AnsweredEntry> = {};
  for (const [id, correct] of Object.entries(old)) {
    answered[id] = { lastChoice: -1, correct: correct === true, at: 0 };
  }
  return { ...raw, answered };
};

const BUILTIN_MIGRATIONS: Readonly<Record<number, Migration>> = { 0: migrateV0 };

/** Storage key: `<namespace>:progress:v1:<licenseClass>` ("v1" = key scheme). */
export function progressKey(licenseClass: string, namespace = 'dcs'): string {
  return `${namespace}:progress:v1:${licenseClass}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isEntry(value: unknown): value is AnsweredEntry {
  return (
    isRecord(value) &&
    typeof value.lastChoice === 'number' &&
    typeof value.correct === 'boolean' &&
    typeof value.at === 'number'
  );
}

function isBookmark(value: unknown): value is BookmarkEntry {
  return (
    isRecord(value) &&
    typeof value.on === 'boolean' &&
    typeof value.at === 'number' &&
    Number.isFinite(value.at)
  );
}

/**
 * Bookmarks of a stored record (F8 T-3.5, optional). Unlike answers, a bad mark only drops itself:
 * a damaged bookmark must not cost the learner their answers. `undefined` when there is none.
 */
function readBookmarks(raw: unknown): Record<string, BookmarkEntry> | undefined {
  if (!isRecord(raw)) return undefined;
  const bookmarks: Record<string, BookmarkEntry> = {};
  for (const [id, mark] of Object.entries(raw)) {
    if (isBookmark(mark)) bookmarks[id] = { on: mark.on, at: mark.at };
  }
  return Object.keys(bookmarks).length === 0 ? undefined : bookmarks;
}

/** Returns a clean `Progress` or `null` when the shape is unusable. */
function validate(raw: Record<string, unknown>, licenseClass: string): Progress | null {
  if (raw.version !== CURRENT_PROGRESS_VERSION) return null;
  if (!isRecord(raw.answered) || !Array.isArray(raw.wrong)) return null;
  const answered: Record<string, AnsweredEntry> = {};
  for (const [id, entry] of Object.entries(raw.answered)) {
    if (!isEntry(entry)) return null;
    answered[id] = { lastChoice: entry.lastChoice, correct: entry.correct, at: entry.at };
  }
  const wrong: string[] = [];
  for (const id of raw.wrong) {
    if (typeof id !== 'string') return null;
    if (!wrong.includes(id)) wrong.push(id);
  }
  const updatedAt = typeof raw.updatedAt === 'number' ? raw.updatedAt : 0;
  const progress: Progress = { version: 1, licenseClass, answered, wrong, updatedAt };
  const bookmarks = readBookmarks(raw.bookmarks);
  if (bookmarks !== undefined) progress.bookmarks = bookmarks;
  return progress;
}

/** Parses, migrates step by step and validates. `null` means "start empty". */
function decode(
  text: string,
  licenseClass: string,
  migrations: Readonly<Record<number, Migration>>,
): Progress | null {
  try {
    let raw: unknown = JSON.parse(text);
    if (!isRecord(raw)) return null;
    let version = typeof raw.version === 'number' ? raw.version : 0;
    // A newer version than we know cannot be read safely: start empty.
    if (version > CURRENT_PROGRESS_VERSION) return null;
    while (version < CURRENT_PROGRESS_VERSION) {
      const step = migrations[version];
      if (step === undefined || !isRecord(raw)) return null;
      raw = step(raw);
      if (!isRecord(raw)) return null;
      version += 1;
      raw = { ...raw, version };
    }
    return isRecord(raw) ? validate(raw, licenseClass) : null;
  } catch {
    return null;
  }
}

/**
 * Drops per-answer detail (choice, time), keeping which ids were answered and how. Bookmarks are
 * kept whole: they are few and their time decides the sync merge.
 */
function compact(progress: Progress): Progress {
  const answered: Record<string, AnsweredEntry> = {};
  for (const [id, entry] of Object.entries(progress.answered)) {
    answered[id] = { lastChoice: -1, correct: entry.correct, at: 0 };
  }
  return { ...progress, answered };
}

/**
 * `ProgressRepo` over a `KeyValueStorage`. Tolerant by design: unreadable or
 * corrupt data becomes an empty record, and storage failures never throw; the
 * last saved value is kept in memory so the session keeps working.
 */
export function createLocalProgressRepo(
  storage: KeyValueStorage,
  options: LocalProgressRepoOptions = {},
): ProgressRepo {
  const namespace = options.namespace ?? 'dcs';
  const maxBytes = options.maxBytes ?? 100_000;
  const migrations = { ...BUILTIN_MIGRATIONS, ...options.migrations };
  const listeners = new Set<ProgressListener>();
  // Last value we wrote, used when the storage cannot be read back.
  const session = new Map<string, Progress>();

  function load(licenseClass: string): Progress {
    let text: string | null = null;
    try {
      text = storage.get(progressKey(licenseClass, namespace));
    } catch {
      return session.get(licenseClass) ?? emptyProgress(licenseClass);
    }
    if (text === null) return session.get(licenseClass) ?? emptyProgress(licenseClass);
    return decode(text, licenseClass, migrations) ?? emptyProgress(licenseClass);
  }

  function save(progress: Progress): Progress {
    let toSave = progress;
    let text = JSON.stringify(toSave);
    if (text.length > maxBytes) {
      toSave = compact(progress);
      text = JSON.stringify(toSave);
    }
    session.set(toSave.licenseClass, toSave);
    try {
      storage.set(progressKey(toSave.licenseClass, namespace), text);
    } catch {
      // Not persisted; `session` keeps the value for this page lifetime.
    }
    for (const listener of listeners) listener(toSave.licenseClass, toSave);
    return toSave;
  }

  return {
    load,
    recordAnswer(licenseClass, questionId, choice, correct, now) {
      const current = load(licenseClass);
      const wrong = current.wrong.filter((id) => id !== questionId);
      if (!correct) wrong.push(questionId);
      return save({
        ...current,
        answered: { ...current.answered, [questionId]: { lastChoice: choice, correct, at: now } },
        wrong,
        updatedAt: now,
      });
    },
    setBookmark(licenseClass, questionId, on, now) {
      // A bookmark is a change of the record like an answer: `updatedAt` moves to `now` too (it is
      // "last local change"; the sync merge takes the larger one, nothing else reads it as "answered").
      const current = load(licenseClass);
      return save({
        ...current,
        bookmarks: { ...current.bookmarks, [questionId]: { on, at: now } },
        updatedAt: now,
      });
    },
    clearWrong(licenseClass, now) {
      return save({ ...load(licenseClass), wrong: [], updatedAt: now });
    },
    reset(licenseClass) {
      session.delete(licenseClass);
      try {
        storage.remove(progressKey(licenseClass, namespace));
      } catch {
        // Nothing durable to clean up.
      }
      const empty = emptyProgress(licenseClass);
      for (const listener of listeners) listener(licenseClass, empty);
      return empty;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
